import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { KEYBOARDS, SCALES, generateKeyboard, genId } from '@/lib/microtonal/scale';
import type { Keyboard, Scale } from '@/lib/microtonal/types';

/**
 * Hook for managing custom (user-saved) keyboards and scales.
 * Built-in presets are merged with user-saved ones.
 */
export function useMicrotonalLibrary() {
  const [customKeyboards, setCustomKeyboards] = useLocalStorage<Keyboard[]>('mf:keyboards', []);
  const [customScales, setCustomScales] = useLocalStorage<Scale[]>('mf:scales', []);
  const [activeKeyboardId, setActiveKeyboardId] = useLocalStorage<string>('mf:active-kb', KEYBOARDS[0]?.id ?? '');

  const allScales = [...SCALES, ...customScales];
  const allKeyboards = [...KEYBOARDS, ...customKeyboards];

  const activeKeyboard = allKeyboards.find((k) => k.id === activeKeyboardId) ?? KEYBOARDS[0];

  const saveKeyboard = useCallback((kb: Keyboard) => {
    setCustomKeyboards((prev) => {
      const exists = prev.some((k) => k.id === kb.id);
      if (exists) {
        return prev.map((k) => (k.id === kb.id ? kb : k));
      }
      return [...prev, kb];
    });
    setActiveKeyboardId(kb.id);
  }, [setCustomKeyboards, setActiveKeyboardId]);

  const deleteKeyboard = useCallback((id: string) => {
    setCustomKeyboards((prev) => prev.filter((k) => k.id !== id));
    if (activeKeyboardId === id) {
      setActiveKeyboardId(KEYBOARDS[0]?.id ?? '');
    }
  }, [setCustomKeyboards, activeKeyboardId, setActiveKeyboardId]);

  const saveScale = useCallback((scale: Scale) => {
    setCustomScales((prev) => {
      const exists = prev.some((s) => s.id === scale.id);
      if (exists) {
        return prev.map((s) => (s.id === scale.id ? scale : s));
      }
      return [...prev, scale];
    });
  }, [setCustomScales]);

  const deleteScale = useCallback((id: string) => {
    setCustomScales((prev) => prev.filter((s) => s.id !== id));
  }, [setCustomScales]);

  const importKeyboard = useCallback((kb: Keyboard) => {
    // Assign a new id to avoid collisions
    const imported: Keyboard = { ...kb, id: genId('kb'), builtIn: false };
    setCustomKeyboards((prev) => [...prev, imported]);
    setActiveKeyboardId(imported.id);
    return imported;
  }, [setCustomKeyboards, setActiveKeyboardId]);

  const createKeyboardFromScale = useCallback((scale: Scale, name?: string) => {
    const kb = generateKeyboard(scale, { name });
    kb.id = genId('kb');
    saveKeyboard(kb);
    return kb;
  }, [saveKeyboard]);

  return {
    allScales,
    allKeyboards,
    customKeyboards,
    customScales,
    activeKeyboard,
    activeKeyboardId,
    setActiveKeyboardId,
    saveKeyboard,
    deleteKeyboard,
    saveScale,
    deleteScale,
    importKeyboard,
    createKeyboardFromScale,
  };
}
